import React, { useMemo } from "react";
import { Grid } from "@material-ui/core";
import { Typography } from "@welldigital/components";
import OrderNotes from "./OrderNotes/OrderNotes";
import OrderDetailsWrapper, { PropsTypes } from "./OrderDetailsWrapper";
import OrderHistoryDetails from "./OrderHistoryDetails/OrderHistoryDetails";
import OrderItemsForReview from "./OrderItemsForReview/OrderItemsForReview";
import IdCheckVerification from "./IdCheckVerification/IdCheckVerification";
import { formatOrderStatus } from "utils/user";
import { formatDate } from "utils/time";
import WarningBox from "./WarningBox";
import SubscriptionDetails from "./SubscriptionDetails/SubscriptionDetails";
import SubscriptionHistory from "./SubscriptionDetails/SubscriptionHistory";
import { formatPrice } from "utils/formatPrice";

const OrderDetails = ({
  details: {
    orderNotes,
    customerId,
    products,
    idCheckStatus,
    idCheckApiStatus = "",
    ...details
  },
  warnings,
  warningsLoading = false,
  orderedForOthersWarning,
  onUpdateSuccess,
  isUpdateOrderModalOpen,
  onUpdateImages,
  refreshOrderDetails,
}: PropsTypes) => {
  const normalizedIdCheckStatus = (idCheckStatus || "").toLowerCase();
  const normalizedIdCheckApiStatus = (idCheckApiStatus || "").toLowerCase();
  const orderId = Number(details.orderId);

  const normalizedOrderDetails = {
    orderNotes,
    customerId,
    products,
    idCheckStatus: normalizedIdCheckStatus,
    idCheckApiStatus: normalizedIdCheckApiStatus,
    ...details,
  };

  const isManualVerificationRequired =
    details.orderStatus.toLowerCase() === "manual verification required";
  const orderStatus =
    isManualVerificationRequired && normalizedIdCheckApiStatus === "failure"
      ? "Awaiting ID check"
      : formatOrderStatus(details.orderStatus);
  const showLNWarning =
    normalizedIdCheckApiStatus === "failure" ||
    (normalizedIdCheckStatus !== "" && normalizedIdCheckStatus !== "pass");

  const reverseOrderNotes = useMemo(
    () => [...(orderNotes ?? [])].reverse(),
    [orderNotes],
  );
  const orderNotesForConfirmBMI = useMemo(
    () => (orderNotes ?? []).filter((note) => note.productId !== 0),
    [orderNotes],
  );
  const shippingMethodClass = details?.shippingMethod
    ?.toLocaleLowerCase()
    ?.includes("express")
    ? "crp_order_details_shippingmethod_express"
    : "crp_order_details_shippingmethod";

  return (
    <div className="crp_order_details">
      <Grid container className="mb-20">
        <Grid item xs={12} sm={6} md={6}>
          <Typography
            variant="h1"
            className="crp_order_details_heading"
            spacingAfter={0}
          >
            Order ID: <b>{details.orderId}</b>
          </Typography>
          <Typography
            variant="h2"
            spacingAfter={0}
            className="crp_order_details_heading"
          >
            Placed: <b>{formatDate(details.orderDate, "dd/MM/yy")}</b>
          </Typography>
          {details?.shippingMethod && (
            <Typography variant="body1" spacingAfter={0}>
              Shipping method:{" "}
              <b className={shippingMethodClass}>{details?.shippingMethod}</b>
            </Typography>
          )}
          {details?.orderAmount && (
            <Typography
              variant="body1"
              spacingAfter={0}
              className="crp_order_details_shippingmethod"
            >
              Order total: <b>{formatPrice(+details?.orderAmount)}</b>
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={6}>
          <Typography
            variant="subtitle2"
            spacingAfter={0}
            className="crp_order_details_status"
          >
            {orderStatus}
          </Typography>
        </Grid>
      </Grid>
      {!!products?.length && (
        <SubscriptionDetails products={products} onSuccess={refreshOrderDetails} />
      )}
      {(warningsLoading || !!warnings.length || showLNWarning) && (
        <WarningBox
          warnings={warnings}
          loading={warningsLoading}
          showLNWarning={showLNWarning}
          idCheckStatus={idCheckStatus}
        />
      )}
      {!!orderedForOthersWarning && (
        <WarningBox
          hideIcon
          title={orderedForOthersWarning.title}
          warnings={[orderedForOthersWarning]}
          idCheckStatus={""}
        />
      )}
      <OrderNotes notes={reverseOrderNotes} orderId={orderId} />
      {(normalizedIdCheckApiStatus !== "" || normalizedIdCheckStatus !== "") && (
        <IdCheckVerification
          orderDetails={normalizedOrderDetails}
          onUpdateSuccess={onUpdateSuccess}
          isUpdateOrderModalOpen={isUpdateOrderModalOpen}
        />
      )}
      {!!products?.length && <SubscriptionHistory products={products} />}

      {customerId && (
        <OrderHistoryDetails orderId={orderId} customerId={customerId} />
      )}
      {products && (
        <OrderItemsForReview
          products={products}
          orderDate={details.orderDate}
          customerId={customerId}
          idCheckStatus={normalizedOrderDetails.idCheckStatus}
          idCheckApiStatus={normalizedOrderDetails.idCheckApiStatus}
          restrictedItemsApproval={normalizedOrderDetails?.restrictedItemsApproval}
          onUpdateImages={onUpdateImages}
          orderNotesForConfirmBMI={orderNotesForConfirmBMI}
        />
      )}
    </div>
  );
};

export default OrderDetailsWrapper(OrderDetails);
